import {useEffect, useRef, useState} from 'react';

/**
 * State that survives a reload.
 *
 * This is the seam where a real backend goes: swap `read`/`write` for fetch
 * calls and nothing above this file changes. Every read is defensive — a user
 * with a half-written or older payload in localStorage must land on the seed
 * data, never on a white screen.
 */
function read<T>(key: string, seed: T): T {
  if (typeof window === 'undefined') return seed;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return seed;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') return seed;
    return parsed as T;
  } catch {
    return seed;
  }
}

export function usePersistentState<T>(key: string, seed: T) {
  const [state, setState] = useState<T>(() => read(key, seed));
  const firstRef = useRef(true);

  useEffect(() => {
    // Skip the write triggered by mount: it would only rewrite what was just
    // read, and it would clobber storage if the component never changes state.
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Private mode / quota exceeded: the workspace still works for this
      // session, it just stops remembering.
    }
  }, [key, state]);

  return [state, setState] as const;
}

let counter = 0;

export function uid(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}
